/**
 * @file ButtonComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de botón
 * -----------------------------------------------------
 * @version 1.0.0a
 * @created 2024-03-03
 * @modified 2024-08-05
 * @author Correcta
 * @copyright © Correcta
 * @license Private
 * */

import React from "react";
import { TYPE_BUTTON } from "@/constants/button";
import { useTranslations } from "next-intl";

interface ButtonComponentProps {
  readonly id?: string;
  readonly label?: string;
  type: "submit" | "reset" | "button";
  onClick?: () => void;
  children?: React.ReactNode;
  status: string;
  className?: string;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  iconPosition?: "left" | "right";
  ariaLabel?: string;
  ariaExpanded?: boolean;
  ariaControls?: string;
  ariaHasPopup?: "menu" | "listbox" | "dialog" | "grid" | "tree" | "true";
  tabIndex?: number;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  noTranslation?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}
const ButtonComponent = ({
  id,
  label,
  type = TYPE_BUTTON,
  onClick,
  children,
  status = "primary",
  className = "",
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  disabled = false,
  iconPosition = "left",
  ariaLabel,
  ariaExpanded,
  ariaControls,
  ariaHasPopup,
  tabIndex,
  onKeyDown,
  noTranslation = false,
  ref,
}: ButtonComponentProps) => {
  const t = useTranslations("Buttons");

  const translatedLabel = label
    ? noTranslation
      ? label
      : t(label)
    : undefined;
  const childrenHasVisibleText =
    typeof children === "string"
      ? children.trim().length > 0
      : typeof children === "number";
  const isIconOnly = !translatedLabel && !childrenHasVisibleText;

  const computedAriaLabel =
    ariaLabel ??
    (isIconOnly ? (translatedLabel ?? t("buttonField")) : undefined);

  const getButtonClass = () => {
    const base = "btn";
    const isOutline = typeof status === "string" && status.includes("outline");
    if (disabled) {
      return `${base} ${isOutline ? "btn--disabled--outline" : "btn--disabled"} ${className}`;
    }
    return `btn btn--${status} ${className}`;
  };

  return (
    <button
      id={id}
      ref={ref}
      type={type}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
      aria-label={
        computedAriaLabel ||
        (typeof children === "string" ? children : undefined)
      }
      aria-expanded={ariaExpanded}
      aria-controls={ariaExpanded ? ariaControls : undefined}
      aria-haspopup={ariaHasPopup}
      className={getButtonClass().trim()}
      tabIndex={tabIndex}
      role="button"
      onKeyDown={onKeyDown}
    >
      {iconPosition === "left" && children}
      {translatedLabel}
      {iconPosition === "right" && children}
    </button>
  );
};

export default ButtonComponent;
